'use client';

import { type ReactNode, useRef, useEffect } from 'react';
import { useTenantStore } from '@/stores/tenant-store';

interface TenantProviderProps {
  tenantId: string | null;
  name: string | null;
  slug?: string | null;
  children: ReactNode;
}

export function TenantProvider({ tenantId, name, slug = null, children }: TenantProviderProps) {
  const store = useTenantStore;
  const initialized = useRef(false);

  // Set store state synchronously on first render so children and tRPC
  // have tenant context immediately. Use setState directly to set raw
  // state values without triggering subscriber notifications during render.
  if (!initialized.current) {
    const nextState =
      tenantId || name || slug
        ? { tenantId, name, slug: slug ?? name?.toLowerCase() ?? null }
        : { tenantId: null, name: null, slug: null };
    // Zustand's setState with replace=true sets state without calling actions,
    // but still notifies. Instead, mutate the internal state object directly
    // for the initial hydration — subscribers pick it up on their first read.
    Object.assign(store.getState(), nextState);
    initialized.current = true;
  }

  // Sync if props change after initial render
  useEffect(() => {
    if (tenantId || name || slug) {
      store.getState().setTenantContext({ tenantId, name, slug });
    } else {
      store.getState().clearTenant();
    }
  }, [tenantId, name, slug, store]);

  return <>{children}</>;
}
